import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import Keycloak from 'keycloak-js';

interface KeycloakContextType {
  keycloak: Keycloak | null;
  initialized: boolean;
  authenticated: boolean;
  token: string | undefined;
  user: KeycloakUser | null;
  login: (options?: Keycloak.KeycloakLoginOptions) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
}

interface KeycloakUser {
  id: string;
  email: string;
  name: string;
  givenName?: string;
  familyName?: string;
  roles: string[];
}

const KeycloakContext = createContext<KeycloakContextType | null>(null);

const keycloakConfig = {
  url: import.meta.env.VITE_KEYCLOAK_URL || 'https://idp.evotechnica.com',
  realm: import.meta.env.VITE_KEYCLOAK_REALM || 'wholeworm',
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'wholeworm-web',
};

export function KeycloakProvider({ children }: { children: ReactNode }) {
  const [keycloak, setKeycloak] = useState<Keycloak | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState<KeycloakUser | null>(null);

  useEffect(() => {
    const kc = new Keycloak(keycloakConfig);

    kc.init({
      onLoad: 'check-sso',
      silentCheckSsoFallback: false,
      pkceMethod: 'S256',
      checkLoginIframe: false,
      enableLogging: true,
    })
      .then((auth) => {
        setKeycloak(kc);
        setAuthenticated(auth);
        setInitialized(true);

        if (auth && kc.tokenParsed) {
          setUser({
            id: kc.tokenParsed.sub || '',
            email: kc.tokenParsed.email || '',
            name: kc.tokenParsed.name || '',
            givenName: kc.tokenParsed.given_name,
            familyName: kc.tokenParsed.family_name,
            roles: kc.tokenParsed.realm_access?.roles || [],
          });

          // Store token for API calls
          if (kc.token) {
            localStorage.setItem('wholeworm-token', kc.token);
          }
        }

        // Set up token refresh
        setInterval(() => {
          kc.updateToken(60)
            .then((refreshed) => {
              if (refreshed) {
                console.log('[WholeWorm] Token refreshed');
                // Update stored token
                if (kc.token) {
                  localStorage.setItem('wholeworm-token', kc.token);
                }
              }
            })
            .catch(() => {
              console.error('[WholeWorm] Token refresh failed');
            });
        }, 30000); // Check every 30 seconds
      })
      .catch((error) => {
        console.error('[WholeWorm] Keycloak init failed:', error);
        setInitialized(true);
      });
  }, []);

  const login = useCallback(
    async (options?: Keycloak.KeycloakLoginOptions) => {
      if (keycloak) {
        await keycloak.login({
          scope: 'openid profile email',
          ...options,
        });
      }
    },
    [keycloak]
  );

  const logout = useCallback(async () => {
    if (keycloak) {
      localStorage.removeItem('wholeworm-token');
      await keycloak.logout({
        redirectUri: window.location.origin,
      });
    }
  }, [keycloak]);

  const refreshToken = useCallback(async (): Promise<boolean> => {
    if (keycloak) {
      try {
        return await keycloak.updateToken(60);
      } catch {
        return false;
      }
    }
    return false;
  }, [keycloak]);

  return (
    <KeycloakContext.Provider
      value={{
        keycloak,
        initialized,
        authenticated,
        token: keycloak?.token,
        user,
        login,
        logout,
        refreshToken,
      }}
    >
      {children}
    </KeycloakContext.Provider>
  );
}

export function useKeycloak() {
  const context = useContext(KeycloakContext);
  if (!context) {
    throw new Error('useKeycloak must be used within a KeycloakProvider');
  }
  return context;
}
